import axios from 'axios';

import { createApiClient } from '@/api/http-client';
import { handleSessionExpired } from '@/api/session-expiry';
import { config } from '@/config';

/** All lifecycle states an async event job can be in. */
export type EventJobStatus =
  'queued' | 'running' | 'waiting' | 'cancelling' | 'succeeded' | 'failed' | 'cancelled';

/** Response body when a job is first enqueued; contains URLs for streaming and status polling. */
export interface EventJobStartResponseDTO {
  job_id: string;
  job_type: string;
  status: EventJobStatus;
  stream_url: string;
  job_stream_url: string;
  status_url: string;
  cancel_url: string;
  websocket_url?: string | null;
  latest_event_id?: number | null;
  latest_global_event_id?: number | null;
}

/** Structured error information embedded in a failed job snapshot or SSE event. */
export interface EventJobErrorDTO {
  code: string;
  message: string;
  retryable: boolean;
}

/** Point-in-time snapshot of a job's status and result, returned by the status URL. */
export interface EventJobSnapshotResponseDTO<T = Record<string, unknown>> {
  job_id: string;
  job_type: string;
  client_id: string;
  project_id?: string | null;
  assessment_id?: string | null;
  created_by: string;
  status: EventJobStatus;
  phase?: string | null;
  progress?: number | null;
  result: T;
  error?: EventJobErrorDTO | null;
  latest_message?: string | null;
  created_at?: string;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at?: string;
}

/** Response returned when a cancellation request is accepted by the backend. */
export interface CancelEventJobResponseDTO {
  job_id: string;
  status: EventJobStatus;
  cancel_requested_at: string;
  message: string;
}

/** A single event emitted by a running job over the SSE stream. */
export interface EventJobEventResponseDTO {
  event_id: number;
  global_event_id: number;
  job_id: string;
  job_type: string;
  status: EventJobStatus;
  phase?: string | null;
  progress?: number | null;
  event_name: string;
  payload: Record<string, unknown>;
  error?: EventJobErrorDTO | null;
}

/** Result of parsing a raw SSE chunk: fully parsed events plus any incomplete trailing bytes. */
export interface ParsedSseFrames {
  events: EventJobEventResponseDTO[];
  buffer: string;
}

/** Typed error thrown by event-job API functions; optionally carries a machine-readable error code. */
export class EventJobApiError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'EventJobApiError';
  }
}

const eventsClient = createApiClient({ baseURL: config.apiUrl, timeout: 15000 });
const STREAM_ATTEMPT_TIMEOUT_MS = 25_000;
const STREAM_RETRY_DELAY_MS = 2_000;
const TERMINAL_EVENT_JOB_STATUSES = new Set<EventJobStatus>(['succeeded', 'failed', 'cancelled']);

/**
 * Resolves a path or full URL returned by the backend into an absolute URL using the configured
 * API origin, stripping the `/api/v1` suffix when the stream paths are root-relative.
 */
const eventUrl = (pathOrUrl: string): string => {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }
  const apiRoot = config.apiUrl.replaceAll(/\/$/g, '');
  const origin = apiRoot.endsWith('/api/v1') ? apiRoot.slice(0, -'/api/v1'.length) : apiRoot;
  const relativePath = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${origin}${relativePath}`;
};

/**
 * Parses a raw SSE text chunk into discrete event objects, leaving any incomplete trailing frame
 * in `buffer` for the next chunk.
 *
 * @param input - Raw SSE text received from the stream reader.
 * @returns Parsed events and the leftover buffer fragment.
 */
export const parseEventJobSseFrames = (input: string): ParsedSseFrames => {
  const normalized = input.replaceAll('\r\n', '\n');
  const frames = normalized.split('\n\n');
  const buffer = frames.pop() ?? '';
  const events: EventJobEventResponseDTO[] = [];

  for (const frame of frames) {
    const dataLines = frame
      .split('\n')
      .filter((line) => line.startsWith('data: '))
      .map((line) => line.slice(6));
    if (dataLines.length === 0) {
      continue;
    }
    const data = dataLines.join('\n').trim();
    if (!data) {
      continue;
    }
    try {
      events.push(JSON.parse(data) as EventJobEventResponseDTO);
    } catch {
      // Skip malformed SSE frame; stream continues
    }
  }

  return { events, buffer };
};

/**
 * Generates a unique client-side operation ID used for job idempotency.
 *
 * @param prefix - Human-readable prefix that identifies the operation type.
 * @returns A string in the form `{prefix}-{uuid}`.
 */
export const createClientOperationId = (prefix: string): string => {
  const randomId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${randomId}`;
};

/** Returns true when the given status represents a terminal (non-retriable) job state. */
const isTerminalEventJobStatus = (status: EventJobStatus): boolean => {
  return TERMINAL_EVENT_JOB_STATUSES.has(status);
};

/**
 * Promise-based delay that resolves cleanly and rejects immediately when the given AbortSignal
 * fires.
 */
const sleep = (ms: number, signal?: AbortSignal): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timeout = globalThis.setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        globalThis.clearTimeout(timeout);
        reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
};

/**
 * Creates a per-attempt AbortSignal that auto-aborts after `STREAM_ATTEMPT_TIMEOUT_MS` and
 * propagates cancellation from the outer caller signal.
 *
 * @returns `{ signal, cleanup }` — call `cleanup()` in a finally block to release resources.
 */
const createAttemptSignal = (
  signal?: AbortSignal,
): { signal: AbortSignal; cleanup: () => void } => {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), STREAM_ATTEMPT_TIMEOUT_MS);
  const abortFromOuterSignal = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', abortFromOuterSignal, { once: true });

  return {
    signal: controller.signal,
    cleanup: () => {
      globalThis.clearTimeout(timeout);
      signal?.removeEventListener('abort', abortFromOuterSignal);
    },
  };
};

/**
 * Polls a job's status URL and returns its current snapshot.
 * GET {statusUrl}
 *
 * @param statusUrl - Full URL or path returned in `EventJobStartResponseDTO.status_url`.
 * @returns The typed job snapshot.
 */
export const getEventJobSnapshot = async <T = Record<string, unknown>>(
  accessToken: string,
  statusUrl: string,
): Promise<EventJobSnapshotResponseDTO<T>> => {
  try {
    const response = await eventsClient.get<EventJobSnapshotResponseDTO<T>>(eventUrl(statusUrl), {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (error) {
    handleSessionExpired(error);
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as Record<string, unknown> | undefined;
      const detail = data?.detail;
      throw new EventJobApiError(
        typeof detail === 'string' ? detail : error.message,
        typeof data?.code === 'string' ? data.code : undefined,
      );
    }
    throw error;
  }
};

/**
 * Sends a cancellation request for a running job.
 * POST {cancelUrl}
 *
 * @param cancelUrl - Full URL or path returned in `EventJobStartResponseDTO.cancel_url`.
 * @returns Cancellation acknowledgement including timestamp.
 */
export const cancelEventJob = async (
  accessToken: string,
  cancelUrl: string,
): Promise<CancelEventJobResponseDTO> => {
  try {
    const response = await eventsClient.post<CancelEventJobResponseDTO>(
      eventUrl(cancelUrl),
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    handleSessionExpired(error);
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as Record<string, unknown> | undefined;
      const detail = data?.detail;
      throw new EventJobApiError(
        typeof detail === 'string' ? detail : error.message,
        typeof data?.code === 'string' ? data.code : undefined,
      );
    }
    throw error;
  }
};

/**
 * Reads a Fetch API `Response` body as an SSE stream, emitting de-duplicated events to `onEvent`
 * until the stream closes or `signal` fires.
 *
 * @param seenEventIds - Mutable set used across retry attempts to de-duplicate events.
 * @param onEvent - Optional callback invoked for each new unique event.
 */
const consumeEventStream = async (
  response: Response,
  signal: AbortSignal,
  seenEventIds: Set<number>,
  onEvent?: (event: EventJobEventResponseDTO) => void,
): Promise<void> => {
  if (!response.ok || !response.body) {
    throw new EventJobApiError(`Unable to open event stream (${response.status}).`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const result = await reader.read();
      if (result.done) {
        return;
      }
      buffer += decoder.decode(result.value, { stream: true });
      const parsed = parseEventJobSseFrames(buffer);
      buffer = parsed.buffer;
      for (const event of parsed.events) {
        if (seenEventIds.has(event.event_id)) {
          continue;
        }
        seenEventIds.add(event.event_id);
        onEvent?.(event);
      }
    }
  } finally {
    reader.releaseLock();
  }
};

/**
 * Returns the snapshot when the job has reached a terminal succeeded or cancelled state,
 * throws `EventJobApiError` if it failed, and returns null when still in progress.
 */
const resolveTerminalSnapshot = <T>(
  snapshot: EventJobSnapshotResponseDTO<T>,
): EventJobSnapshotResponseDTO<T> | null => {
  if (snapshot.status === 'failed') {
    throw new EventJobApiError(
      snapshot.error?.message || snapshot.latest_message || 'Event job failed.',
      snapshot.error?.code,
    );
  }
  return isTerminalEventJobStatus(snapshot.status) ? snapshot : null;
};

/**
 * Streams a job's SSE events until it reaches a terminal state, automatically reconnecting on
 * stream drops, then resolves with the final typed snapshot.
 *
 * @param start - Job handle returned by the enqueue call.
 * @param onEvent - Optional callback invoked for each SSE event.
 * @param signal - Optional AbortSignal to cancel waiting early.
 * @returns The completed job snapshot containing the typed result.
 */
export async function waitForEventJob<T = Record<string, unknown>>(
  accessToken: string,
  start: EventJobStartResponseDTO,
  onEvent?: (event: EventJobEventResponseDTO) => void,
  signal?: AbortSignal,
): Promise<EventJobSnapshotResponseDTO<T>> {
  const seenEventIds = new Set<number>();
  // Start at 0 so the first connection never sends Last-Event-ID (avoids a CORS preflight for
  // that header on the initial request). After we receive events the tracker advances and
  // subsequent reconnects will send Last-Event-ID to skip already-delivered events.
  let lastSeenEventId = 0;

  while (!signal?.aborted) {
    const attempt = createAttemptSignal(signal);
    const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
    if (lastSeenEventId > 0) {
      headers['Last-Event-ID'] = String(lastSeenEventId);
    }
    try {
      const response = await fetch(eventUrl(start.job_stream_url), {
        headers,
        signal: attempt.signal,
      });
      const trackingOnEvent = (event: EventJobEventResponseDTO): void => {
        lastSeenEventId = Math.max(lastSeenEventId, event.event_id);
        onEvent?.(event);
      };
      await consumeEventStream(response, attempt.signal, seenEventIds, trackingOnEvent);
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }
      if (error instanceof EventJobApiError) {
        throw error;
      }
    } finally {
      attempt.cleanup();
    }

    const snapshot = await getEventJobSnapshot<T>(accessToken, start.status_url);
    const terminalSnapshot = resolveTerminalSnapshot(snapshot);
    if (terminalSnapshot) {
      return terminalSnapshot;
    }
    await sleep(STREAM_RETRY_DELAY_MS, signal);
  }

  throw signal.reason ?? new DOMException('Aborted', 'AbortError');
}
